import { createColumnHelper } from '@tanstack/react-table';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  type LineItem,
  useGetInvoiceLineItems,
} from '~entities/payment/api/get-invoice-line-items';
import { useUpdateSubscriptionInvoiceLineItems } from '~entities/student/api/update-subscription-invoices';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

interface EditPendingPaymentProps {
  invoiceId: string;
  date: string;
  studentName: string;
  productName?: string;
}
const columnHelper = createColumnHelper<LineItem>();

export const EditPendingPayment = ({
  invoiceId,
  date,
  studentName,
  productName,
}: EditPendingPaymentProps) => {
  // State for modal open/close
  const [open, setOpen] = useState(false);
  const [editingLineItemId, setEditingLineItemId] = useState<string | null>(
    null,
  );
  const [editingLineItemAmount, setEditingLineItemAmount] = useState('');

  const { data: currencyDetails } = useGetCurrency();

  const { data, isFetching } = useGetInvoiceLineItems({
    invoiceId,
  });

  const { mutateAsync: updateSubscriptionInvoiceLineItems, isPending } =
    useUpdateSubscriptionInvoiceLineItems();

  const total =
    data?.reduce((sum, item) => sum + parseFloat(item.amount), 0) ?? 0;

  const handleSaveLineItem = async (item: LineItem) => {
    const lineItem = {
      productName: item.productName,
      amount: Number(editingLineItemAmount),
      type: item.type,
    };

    try {
      await updateSubscriptionInvoiceLineItems({
        invoiceId: item.invoiceId,
        lineItems: [lineItem],
      });
      setEditingLineItemId(null);
      setEditingLineItemAmount('');
      toast.success('Line item updated successfully');
    } catch (error) {
      console.error('Error updating line item:', error);
      toast.error('Failed to update line item');
    }
  };

  const handleEditLineItem = (item: LineItem) => {
    setEditingLineItemId(`${item.invoiceId}${item.type}`);
    setEditingLineItemAmount(item.amount);
  };
  const columns = [
    columnHelper.accessor('type', { header: 'Description' }),
    columnHelper.accessor('amount', {
      header: 'Price',
      cell: ({ row }) =>
        editingLineItemId ===
        `${row.original.invoiceId}${row.original.type}` ? (
          <div className='flex items-center gap-2'>
            <Input
              type='number'
              min='0'
              className='h-8 w-24'
              value={editingLineItemAmount}
              onChange={(event) => {
                const value = event.target.value;
                setEditingLineItemAmount(
                  value === '' || Number(value) < 0 || value === '-'
                    ? '0'
                    : value,
                );
              }}
              autoFocus
            />
            <Button
              size='sm'
              variant='ghost'
              onClick={() => {
                setEditingLineItemId(null);
              }}
              disabled={isFetching || isPending}
            >
              <X className='size-4 text-red-500' />
            </Button>
            <Button
              size='sm'
              variant='ghost'
              onClick={() => {
                void handleSaveLineItem(row.original);
              }}
            >
              {isPending || isFetching ? (
                <Loader2 className='size-4 animate-spin' />
              ) : (
                <Check className='size-4 text-green-500' />
              )}
            </Button>
          </div>
        ) : (
          `${currencyDetails?.symbol ?? '$'}${row.original.amount}`
        ),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant='ghost'
          size='icon'
          onClick={() => {
            handleEditLineItem(row.original);
          }}
        >
          <Pencil />
          <span className='sr-only'>Edit line item</span>
        </Button>
      ),
    }),
  ];

  return (
    <div className='p-4'>
      <Button
        onClick={() => {
          setOpen(true);
        }}
        variant={'ghost'}
        className='h-fit p-0'
      >
        <Pencil />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='sm:max-w-[600px]'>
          <DialogHeader>
            <DialogTitle>Edit Student Information</DialogTitle>
          </DialogHeader>

          <div className='grid gap-6 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='studentName' className='text-right'>
                Student Name
              </Label>
              <Input
                id='studentName'
                value={studentName}
                className='col-span-3'
                disabled
              />
            </div>

            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='productName' className='text-right'>
                Product Name
              </Label>
              <Input
                id='productName'
                value={productName ?? 'N/A'}
                placeholder='No product selected'
                className='col-span-3'
                disabled
              />
            </div>

            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='date' className='text-right'>
                Date
              </Label>
              <Input
                id='date'
                value={date}
                placeholder='No date selected'
                className='col-span-3'
                disabled
              />
            </div>

            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <h3 className='text-lg font-medium'>Line Items</h3>
              </div>

              <EnrollioTable
                totalLabel='Line items'
                filterHeader={(table) => (
                  <EnrollioTableSearch
                    table={table}
                    placeholder='Search line items...'
                  />
                )}
                data={data ?? []}
                columns={columns}
                getRowId={(row, index) =>
                  `${row.invoiceId}-${row.type}-${String(index)}`
                }
                isUpdating={isFetching || isPending}
                paginationVariant='compact'
                emptyContent='No line items.'
                ariaLabel='Pending payment line items'
              />

              <div className='flex items-center justify-end pt-4'>
                <div className='text-lg font-medium'>
                  Total: {currencyDetails?.symbol}
                  {total.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Here you would typically save the data to
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
